/* eslint-disable prettier/prettier */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Box,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Tabs,
  Tab,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { makeStyles } from '@material-ui/styles';
import { withStyles } from '@material-ui/core/styles';
import {
  BASE_S3,
  BUTTON_BACKGROUND_COLOR,
  BUTTON_TEXT_COLOR,
  GENERAL_TEXT_COLOR,
  SIGNED_IN_NAV_TEXT_COLOR,
  NAV_FONT_FAMILY,
} from 'config/consts';
import DOMPurify from 'dompurify';
import { DomPurifyContentContainer } from './styles';
import ReactPlayer from 'react-player';
import LockIcon from '@material-ui/icons/Lock';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    padding: theme.spacing(2),
  },
  descriptionContainer: {
    marginBottom: '1.5rem',
  },
  productCard: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[4],
    },
  },
  productCardSelected: {
    border: `2px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.primary.light + '10',
  },
  productImage: {
    height: 160,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
  productPrice: {
    fontWeight: 'bold',
    color: theme.palette.primary.main,
    fontSize: '1.25rem',
  },
  originalPrice: {
    textDecoration: 'line-through',
    color: theme.palette.text.secondary,
    fontSize: '0.9rem',
    marginRight: theme.spacing(1),
  },
  discountBadge: {
    backgroundColor: '#e53935',
    color: 'white',
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: '0.75rem',
    fontWeight: 'bold',
  },
  stockInfo: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  buyButton: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1),
    fontSize: '0.95rem',
    fontWeight: 'bold',
  },
  recurrenceInfo: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    fontWeight: 'bold',
  },
  securityInfo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  securityIcon: {
    marginRight: theme.spacing(1),
    color: '#4caf50',
  },
  productModalImage: {
    width: '100%',
    maxHeight: 400,
    objectFit: 'contain',
    marginBottom: theme.spacing(2),
  },
  productModalPrice: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
    marginTop: theme.spacing(2),
  },
  productModalOriginalPrice: {
    textDecoration: 'line-through',
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(1),
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
}));

const StyledTabs = withStyles({
  indicator: {
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    '& > div': {
      width: '100%',
      backgroundColor: SIGNED_IN_NAV_TEXT_COLOR,
    },
  },
})((props) => <Tabs {...props} TabIndicatorProps={{ children: <div /> }} />);

const StyledTab = withStyles((theme) => ({
  root: {
    textTransform: 'none',
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.typography.pxToRem(15),
    marginRight: theme.spacing(1),
    '&:focus': {
      opacity: 1,
      outline: 'none !important',
    },
    '&:focus-visible': {
      outline: 'none !important',
    },
    outline: '0 !important',
    padding: '0 !important',
    display: 'flex',
    minWidth: '20px !important',
    fontFamily: NAV_FONT_FAMILY,
    border: '1px solid transparent !important',
    borderRadius: 0,
    boxShadow: 'none !important',
    backgroundColor: 'transparent',
  },
  selected: {
    border: '1px solid transparent !important',
    borderRadius: 0,
    boxShadow: 'none !important',
    backgroundColor: 'transparent',
  },
}))((props) => <Tab disableRipple {...props} />);

function ActivityVendasOnlineParceiro({ activities, auth, history }) {
  const classes = useStyles();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [infoTabIndex, setInfoTabIndex] = useState(0);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [productModalData, setProductModalData] = useState(null);

  const activity = activities.detail;
  
  // Parse body para obter configurações adicionais
  let parsedBody = {};
  try {
    if (activity?.body) {
      parsedBody = typeof activity.body === 'string' ? JSON.parse(activity.body) : activity.body;
    }
  } catch (e) {
    console.warn('Erro ao parsear body:', e);
  }
  const rawProducts = activity?.products || parsedBody.products || [];
  const products = rawProducts.map(p => ({
    id: p.id,
    name: p.name,
    description: p.description,
    price: parseFloat(p.value || p.price) || 0,
    discount: parseFloat(p.discount_value || p.discount) || 0,
    stock: p.stock,
    limit: p.limit || p.limit_per_user,
    image: p.image,
    order: p.order,
  }));
  
  const recurrenceType = parsedBody.recurrence_type || 'single';
  const allowInstallments = parsedBody.allow_installments || false;
  const installmentsCount = parsedBody.installments_count || 12;

  useEffect(() => {
    if (products.length === 1 && !selectedProduct) {
      setSelectedProduct(products[0]);
    }
  }, [products]);

  const getRecurrenceLabel = (type) => {
    const labels = {
      single: 'Compra única',
      weekly: 'Assinatura Semanal',
      biweekly: 'Assinatura Quinzenal',
      monthly: 'Assinatura Mensal',
      bimonthly: 'Assinatura Bimestral',
      quarterly: 'Assinatura Trimestral',
      semiannual: 'Assinatura Semestral',
      annual: 'Assinatura Anual',
    };
    return labels[type] || type;
  };

  const formatCurrency = (value) => {
    return `R$ ${(value || 0).toFixed(2).replace('.', ',')}`;
  };

  const getDiscountedPrice = (product) => {
    if (product.discount && product.discount > 0) {
      return product.price - product.discount;
    }
    return product.price;
  };

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setQuantity(1);
  };

  const handleOpenProductModal = (product, e) => {
    e.stopPropagation();
    handleSelectProduct(product);
    setProductModalData(product);
    setProductModalOpen(true);
  };

  const handleCloseProductModal = () => {
    setProductModalOpen(false);
    setProductModalData(null);
  };

  const handleBuy = async () => {
    if (!selectedProduct && products.length > 1) return;

    const productToBuy = selectedProduct || products[0];
    
    setLoading(true);
    try {
      const productIndex = products.findIndex(p => Number(p.id) === Number(productToBuy.id));
      const paymentUrl = `/homepage/pagamento/${activity.id}?product_index=${productIndex}&quantity=${quantity}`;
      history.push(paymentUrl);
    } catch (error) {
      console.error('Erro ao processar compra:', error);
    } finally {
      setLoading(false);
    }
  };

  const canBuy = (product) => {
    if (!product) return products.length === 1;
    if (product.stock !== null && product.stock !== undefined && product.stock <= 0) return false;
    return true;
  };

  // Conteúdos de descrição/regulamento/termos vindos da atividade
  const descriptionHtml = parsedBody.description || activity?.description || '';
  const rulesHtml = parsedBody.rules || activity?.rules || '';
  const termsHtml = parsedBody.terms || '';

  const hasDescription = !!(descriptionHtml && descriptionHtml.trim());
  const hasRules = !!(rulesHtml && rulesHtml.trim());
  const hasTerms = !!(termsHtml && termsHtml.trim());

  const infoTabs = [];
  if (hasDescription) {
    infoTabs.push({ key: 'description', label: 'Geral', content: descriptionHtml });
  }
  if (hasRules) {
    infoTabs.push({ key: 'rules', label: 'Regulamento', content: rulesHtml });
  }
  if (hasTerms) {
    infoTabs.push({ key: 'terms', label: 'Termos e condições', content: termsHtml });
  }

  return (
    <Grid item container xs={12} justifyContent="center" alignItems="center">
      {/* Descrição, Regulamento e Termos em Abas */}
      {infoTabs.length > 0 && (
        <Grid
          item
          container
          justifyContent="center"
          alignItems="center"
          xs={12}
          className={classes.descriptionContainer}
        >
          {infoTabs.length === 1 ? (
            <Typography variant="body1" component="div">
              <DomPurifyContentContainer
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(infoTabs[0].content),
                }}
              />
            </Typography>
          ) : (
            <Box width="100%">
              <StyledTabs
                value={infoTabIndex}
                onChange={(e, newValue) => setInfoTabIndex(newValue)}
                variant="scrollable"
                scrollButtons="auto"
              >
                {infoTabs.map((tab, index) => (
                  <StyledTab
                    key={tab.key}
                    label={tab.label}
                    style={{
                      color:
                        index === infoTabIndex ? GENERAL_TEXT_COLOR : SIGNED_IN_NAV_TEXT_COLOR,
                      marginRight: index === 0 ? '1rem' : undefined,
                    }}
                  />
                ))}
              </StyledTabs>
              <Box mt={2}>
                <Typography variant="body1" component="div">
                  <DomPurifyContentContainer
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(infoTabs[infoTabIndex].content),
                    }}
                  />
                </Typography>
              </Box>
            </Box>
          )}
        </Grid>
      )}

      {/* Vídeos da Atividade */}
      {activity?.action_videos?.length > 0 && (
        <Grid item container xs={12} justifyContent="center" style={{ marginBottom: '1.5rem' }}>
          {activity.action_videos.map((actionVideo, index) => (
            <Grid item xs={12} md={8} key={index} style={{ marginBottom: '1rem' }}>
              <ReactPlayer
                url={actionVideo.video?.url}
                width="100%"
                height="300px"
                controls
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Informações de Recorrência */}
      {recurrenceType !== 'single' && (
        <Grid item xs={12}>
          <Box className={classes.recurrenceInfo}>
            <Typography variant="body2">
              <strong>Tipo:</strong> {getRecurrenceLabel(recurrenceType)}
            </Typography>
            {allowInstallments && (
              <Typography variant="body2">
                Parcelamento em até {installmentsCount}x sem juros
              </Typography>
            )}
          </Box>
        </Grid>
      )}

      {/* Lista de Produtos */}
      {products.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="h6" className={classes.sectionTitle}>
            {products.length === 1 ? 'Produto' : 'Produtos Disponíveis'}
          </Typography>

          <Grid container spacing={2}>
            {products.map((product, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  className={`${classes.productCard} ${
                    Number(selectedProduct?.id) === Number(product.id) ? classes.productCardSelected : ''
                  }`}
                  onClick={(e) => handleOpenProductModal(product, e)}
                >
                  {product.image && (
                    <CardMedia
                      className={classes.productImage}
                      image={`${BASE_S3}/${product.image}`}
                      title={product.name}
                    />
                  )}
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 'bold' }}>
                      {product.name || `Produto ${index + 1}`}
                    </Typography>
                    
                    {product.description && (
                      <Typography variant="body2" color="textSecondary" paragraph>
                        {product.description.length > 100 
                          ? product.description.substring(0, 100) + '...' 
                          : product.description}
                      </Typography>
                    )}

                    <Box display="flex" alignItems="center" flexWrap="wrap">
                      {product.discount > 0 && (
                        <>
                          <Typography className={classes.originalPrice}>
                            {formatCurrency(product.price)}
                          </Typography>
                          <span className={classes.discountBadge}>
                            -{formatCurrency(product.discount)}
                          </span>
                        </>
                      )}
                    </Box>
                    
                    <Typography className={classes.productPrice}>
                      {formatCurrency(getDiscountedPrice(product))}
                    </Typography>

                    {allowInstallments && (
                      <Typography variant="caption" color="textSecondary">
                        ou {installmentsCount}x de {formatCurrency(getDiscountedPrice(product) / installmentsCount)}
                      </Typography>
                    )}

                    {product.stock !== null && product.stock !== undefined && (
                      <Typography className={classes.stockInfo}>
                        {product.stock > 0 ? `${product.stock} em estoque` : 'Esgotado'}
                      </Typography>
                    )}

                    {product.limit > 0 && (
                      <Typography className={classes.stockInfo}>
                        Limite: {product.limit} por pessoa
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
      )}

      {/* Área de Compra */}
      {(selectedProduct || products.length === 1) && (
        <Grid item xs={12} md={8}>
          <Box>
            <Button
              variant="contained"
              fullWidth
              className={classes.buyButton}
              onClick={handleBuy}
              disabled={loading || !canBuy(selectedProduct || products[0])}
              style={{
                backgroundColor: BUTTON_BACKGROUND_COLOR,
                color: BUTTON_TEXT_COLOR,
              }}
              startIcon={loading ? null : <ShoppingCartIcon />}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                recurrenceType === 'single' ? 'Pagar' : 'Assinar Agora'
              )}
            </Button>

            {/* Informação de Segurança */}
            <Box className={classes.securityInfo}>
              <LockIcon className={classes.securityIcon} fontSize="small" />
              <Typography variant="caption">
                Ambiente seguro - SSL Certificado
              </Typography>
            </Box>
          </Box>
        </Grid>
      )}

      {/* Botão Pagar quando não há produtos (link de pagamento simples) */}
      {/* Modal de Detalhes do Produto */}
      <Dialog
        open={productModalOpen}
        onClose={handleCloseProductModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{productModalData?.name}</Typography>
            <IconButton
              className={classes.closeButton}
              onClick={handleCloseProductModal}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {productModalData?.image && (
            <img
              src={`${BASE_S3}/${productModalData.image}`}
              alt={productModalData.name}
              className={classes.productModalImage}
            />
          )}
          
          <Typography variant="body1" paragraph style={{ color: productModalData?.description ? 'inherit' : '#999' }}>
            {productModalData?.description || 'Sem descrição disponível'}
          </Typography>

          <Box display="flex" alignItems="center" flexWrap="wrap">
            {productModalData?.discount > 0 && (
              <>
                <Typography className={classes.productModalOriginalPrice}>
                  {formatCurrency(productModalData.price)}
                </Typography>
                <span className={classes.discountBadge}>
                  -{formatCurrency(productModalData.discount)}
                </span>
              </>
            )}
          </Box>
          
          <Typography className={classes.productModalPrice}>
            {formatCurrency(getDiscountedPrice(productModalData || {}))}
          </Typography>

          {allowInstallments && productModalData && (
            <Typography variant="body2" color="textSecondary">
              ou {installmentsCount}x de {formatCurrency(getDiscountedPrice(productModalData) / installmentsCount)}
            </Typography>
          )}

          {productModalData?.stock !== null && productModalData?.stock !== undefined && (
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              {productModalData.stock > 0 ? `${productModalData.stock} em estoque` : 'Esgotado'}
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {products.length === 0 && (
        <Grid item xs={12} md={6}>
          <Box>
            <Button
              variant="contained"
              fullWidth
              className={classes.buyButton}
              onClick={handleBuy}
              disabled={loading}
              style={{
                backgroundColor: BUTTON_BACKGROUND_COLOR,
                color: BUTTON_TEXT_COLOR,
              }}
              startIcon={loading ? null : <ShoppingCartIcon />}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                activity?.click_me_label || 'Pagar'
              )}
            </Button>
            <Box className={classes.securityInfo}>
              <LockIcon className={classes.securityIcon} fontSize="small" />
              <Typography variant="caption">
                Ambiente seguro - SSL Certificado
              </Typography>
            </Box>
          </Box>
        </Grid>
      )}
    </Grid>
  );
}

ActivityVendasOnlineParceiro.propTypes = {
  activities: PropTypes.object.isRequired,
  auth: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  activities: state.activities,
  auth: state.auth,
});

export default connect(mapStateToProps)(ActivityVendasOnlineParceiro);
